import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  ImageList,
  ImageListItem,
  Container,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Person as PersonIcon,
  School as EducationIcon,
  Work as WorkIcon,
  CreditCard as IdCardIcon,
  AccountBalance as BankIcon,
  Home as AddressIcon,
  Email,
  Phone,
  Cake,
  Public,
  Female,
  Male,
  Transgender,
  Bloodtype,
  Language,
  Translate,
  Fingerprint,
  Category,
  TempleHindu,
  Accessibility,
  Favorite,
  FavoriteBorder,
  CalendarToday,
  LocationOn,
  Business,
  AccountBalance,
  HomeWork,
  Map,
  Download as DownloadIcon
} from '@mui/icons-material';
import CryptoJS from 'crypto-js';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const SharedProfilePage = () => {
  const { token } = useParams();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [profileData, setProfileData] = React.useState(null);
  const [timeLeft, setTimeLeft] = React.useState('');

  const ENCRYPTION_KEY = "MoonaConsultancySecretKey";

  // Function to decode HTML entities
  const decodeHtmlEntities = (text) => {
    if (!text) return text;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  // Function to decrypt encrypted data
  const decryptData = (encryptedText) => {
    if (!encryptedText || encryptedText.trim() === "") return "";
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedText, ENCRYPTION_KEY);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      return decrypted || encryptedText;
    } catch (error) {
      return encryptedText;
    }
  };

  React.useEffect(() => {
    if (token) {
      fetchSharedProfile();
    }
  }, [token]);

  // Countdown timer effect
  React.useEffect(() => {
    if (profileData?.expiresAt) {
      const updateTimer = () => {
        const now = new Date().getTime();
        const expiryTime = new Date(profileData.expiresAt).getTime();
        const difference = expiryTime - now;

        if (difference > 0) {
          const days = Math.floor(difference / (1000 * 60 * 60 * 24));
          const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((difference % (1000 * 60)) / 1000);

          let timerText = '';
          if (days > 0) timerText += `${days}d `;
          if (hours > 0 || days > 0) timerText += `${hours}h `;
          if (minutes > 0 || hours > 0 || days > 0) timerText += `${minutes}m `;
          timerText += `${seconds}s`;

          setTimeLeft(timerText.trim());
        } else {
          setTimeLeft('Expired');
        }
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    }
  }, [profileData?.expiresAt]);

  const fetchSharedProfile = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(
        `https://seltentechnologiesdemo.com/moona-backend/getSharedProfile.php?token=${token}`
      );
      
      const data = await response.json();
      
      if (data.status === 'success') {
        setProfileData(data);
        console.log(data);
      } else {
        setError(data.message || 'Failed to load shared profile');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getFieldIcon = (fieldName) => {
    const iconMap = {
      name: <PersonIcon />,
      email: <Email />,
      mobile: <Phone />,
      dob_c: <Cake />,
      age_c: <Cake />,
      gender_c: (value) => {
        if (value?.toLowerCase() === 'female') return <Female />;
        if (value?.toLowerCase() === 'male') return <Male />;
        return <Transgender />;
      },
      bloodgroup_c: <Bloodtype />,
      birthplace_c: <Public />,
      mothertongue_c: <Translate />,
      languagesknown_c: <Language />,
      aadhaar_c: <Fingerprint />,
      caste_c: <Category />,
      religion_c: <TempleHindu />,
      disability_c: <Accessibility />,
      alivestatus_c: (value) => value?.toLowerCase() === 'alive' ? <Favorite /> : <FavoriteBorder />,
      dateofdeath_c: <CalendarToday />,
      deathplace_c: <LocationOn />,
      deathreason_c: <Business />,
      deathaddress_c: <HomeWork />,
      deatharea_c: <Map />,
      deathpincode_c: <Map />,
      deathcity_c: <LocationOn />,
      deathstate_c: <Public />,
      deathcountry_c: <Public />,
      maritalstatus_c: <PersonIcon />
    };
    
    const icon = iconMap[fieldName];
    if (typeof icon === 'function' && profileData?.actualData?.basicInfo?.[fieldName]) {
      return icon(profileData.actualData.basicInfo[fieldName]);
    }
    return icon || <PersonIcon />;
  };

  const formatFieldLabel = (fieldName) => {
    const labelMap = {
      name: 'Full Name',
      email: 'Email Address',
      mobile: 'Mobile Number',
      dob_c: 'Date of Birth',
      age_c: 'Age',
      gender_c: 'Gender',
      bloodgroup_c: 'Blood Group',
      birthplace_c: 'Birth Place',
      mothertongue_c: 'Mother Tongue',
      languagesknown_c: 'Languages Known',
      aadhaar_c: 'Aadhaar Number',
      caste_c: 'Caste',
      religion_c: 'Religion',
      disability_c: 'Disability',
      alivestatus_c: 'Alive Status',
      dateofdeath_c: 'Date of Death',
      deathplace_c: 'Death Place',
      deathreason_c: 'Death Reason',
      deathaddress_c: 'Death Address',
      deatharea_c: 'Death Area',
      deathpincode_c: 'Death Pincode',
      deathcity_c: 'Death City',
      deathstate_c: 'Death State',
      deathcountry_c: 'Death Country',
      maritalstatus_c: 'Marital Status'
    };
    return labelMap[fieldName] || fieldName.replace(/_c$/, '').replace(/([A-Z])/g, ' $1');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN');
    } catch {
      return dateString;
    }
  };

  const getImageUrl = (imageId) => {
    if (!imageId) return null;
    return `https://seltentechnologiesdemo.com/moona-backend/getImage.php?id=${imageId}`;
  };

  // Function to generate PDF from profile details (excluding header)
const generatePDF = async () => {
  try {
    // Show loading indicator
    const loadingAlert = document.createElement('div');
    loadingAlert.innerHTML =
      '<div style="position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:#000;color:#fff;padding:20px;border-radius:8px;z-index:9999;">Generating PDF...</div>';
    document.body.appendChild(loadingAlert);

    // Get content
    const contentElement = document.querySelector('[data-pdf-content]');
    if (!contentElement) {
      document.body.removeChild(loadingAlert);
      alert('No content found to export.');
      return;
    }

    // Extract only text content
    const textContent = contentElement.innerText || contentElement.textContent;

    // Create PDF
    const pdf = new jsPDF('p', 'mm', 'a4');
    const margin = 15;
    const pageWidth = pdf.internal.pageSize.getWidth() - margin * 2;
    const pageHeight = pdf.internal.pageSize.getHeight() - margin * 2;
    const lineHeight = 8;

    // Split long text into lines that fit the page width
    const lines = pdf.splitTextToSize(textContent.trim(), pageWidth);

    let y = margin;
    let pageNum = 1;

    lines.forEach((line) => {
      if (y + lineHeight > pageHeight + margin) {
        pdf.addPage();
        y = margin;
        pageNum++;
      }
      pdf.text(line, margin, y);
      y += lineHeight;
    });

    // Remove loading
    document.body.removeChild(loadingAlert);

    // Save file
    const userName = window.userName || 'Profile';
    const fileName = `${userName}_Details_${new Date()
      .toISOString()
      .split('T')[0]}.pdf`;
    pdf.save(fileName);
  } catch (error) {
    const loadingAlert = document.querySelector('div[style*="Generating PDF"]');
    if (loadingAlert) document.body.removeChild(loadingAlert);
    alert('Error generating PDF. Please try again. Error: ' + error.message);
  }
};



  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading shared profile...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!profileData) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No profile data found.
      </Alert>
    );
  }

  const { actualData, userName, expiresAt, userId, allowDownload } = profileData;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 4, mb: 4, backgroundColor: '#f8f9fa', textAlign: 'center', position: 'relative' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ flex: 1 }} />
          <Typography variant="h3" gutterBottom color="primary" fontWeight="bold">
            {userName} Details
          </Typography>
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
            {timeLeft !== 'Expired' && allowDownload && (
              <Tooltip title="Download PDF">
                <IconButton 
                  onClick={generatePDF}
                  sx={{ 
                    bgcolor: 'primary.main', 
                    '&:hover': { bgcolor: 'primary.dark' },
                    mr: 1
                  }}
                  data-pdf-exclude
                >
                  <DownloadIcon sx={{ color: '#ffffff !important' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        
        {/* Highlighted Expiry Timer */}
        <Box 
          sx={{ 
            display: 'inline-block',
            p: 2,
            borderRadius: 2,
            backgroundColor: timeLeft === 'Expired' ? '#ffebee' : '#fff3e0',
            border: `2px solid ${timeLeft === 'Expired' ? '#f44336' : '#ff9800'}`,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            animation: timeLeft !== 'Expired' ? 'pulse 2s infinite' : 'none',
            '@keyframes pulse': {
              '0%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.02)' },
              '100%': { transform: 'scale(1)' }
            }
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              color: timeLeft === 'Expired' ? 'error.main' : 'warning.main',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <CalendarToday sx={{ fontSize: '1.2rem' }} />
            {timeLeft === 'Expired' ? 'Profile Expired' : `Expires in: ${timeLeft}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {new Date(expiresAt).toLocaleString(undefined, { 
              year: 'numeric', 
              month: '2-digit', 
              day: '2-digit', 
              hour: '2-digit', 
              minute: '2-digit', 
              second: '2-digit', 
              hour12: true 
            })}
          </Typography>
        </Box>
      </Paper>

      {/* Check if profile is expired */}
      {timeLeft === 'Expired' ? (
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h4" color="error" gutterBottom>
              Profile Expired
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              This shared profile has expired and is no longer accessible.
            </Typography>
            <Typography variant="body1">
              Please ask the profile owner to resend the profile link.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box data-pdf-content>
          {/* Basic Information */}
          {actualData.basicInfo && Object.keys(actualData.basicInfo).length > 0 && (
            <Card sx={{ mb: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <PersonIcon sx={{ mr: 2, fontSize: '2rem' }} />
                  Basic Information
                </Typography>
                <Grid container spacing={3}>
                  {Object.entries(actualData.basicInfo).map(([field, value]) => (
                    value && (
                      <Grid item xs={12} sm={6} md={4} key={field}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', p: 2, borderRadius: 2, backgroundColor: '#f8f9fa' }}>
                          <ListItemIcon sx={{ minWidth: 48, color: 'primary.main' }}>
                            {getFieldIcon(field)}
                          </ListItemIcon>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary" display="block" fontWeight="medium">
                              {formatFieldLabel(field)}
                            </Typography>
                            <Typography variant="body1" fontWeight="medium" sx={{ mt: 0.5 }}>
                              {decodeHtmlEntities(value) || 'Not provided'}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Address Details */}
          {actualData.addressDetails.length > 0 && (
            <Card sx={{ mb: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <AddressIcon sx={{ mr: 2, fontSize: '2rem' }} />
                  Addresses ({actualData.addressDetails.length})
                </Typography>
                <Grid container spacing={3}>
                  {actualData.addressDetails.map((address, index) => (
                    <Grid item xs={12} md={6} key={address.id || index}>
                      <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                            <AddressIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="h6">
                              {address.addresstype_c === 'Current' ? 'Current Address' : 'Permanent Address'}
                            </Typography>
                            {address.addresstype_c && (
                              <Chip 
                                label={address.addresstype_c} 
                                size="small"
                                color={address.addresstype_c === 'Current' ? 'primary' : 'secondary'}
                              />
                            )}
                          </Box>
                        </Box>
                        
                        <Box sx={{ lineHeight: 1.8 }}>
                          {address.address && <Typography variant="body2"><strong>Address:</strong> {decodeHtmlEntities(address.address)}</Typography>}
                          {address.street && <Typography variant="body2"><strong>Street:</strong> {decodeHtmlEntities(address.street)}</Typography>}
                          {address.village && <Typography variant="body2"><strong>Village:</strong> {decodeHtmlEntities(address.village)}</Typography>}
                          {address.taluk && <Typography variant="body2"><strong>Taluk:</strong> {decodeHtmlEntities(address.taluk)}</Typography>}
                          {address.district && <Typography variant="body2"><strong>District:</strong> {decodeHtmlEntities(address.district)}</Typography>}
                          {address.city && <Typography variant="body2"><strong>City:</strong> {decodeHtmlEntities(address.city)}</Typography>}
                          {address.state && <Typography variant="body2"><strong>State:</strong> {decodeHtmlEntities(address.state)}</Typography>}
                          {address.country && <Typography variant="body2"><strong>Country:</strong> {decodeHtmlEntities(address.country)}</Typography>}
                          {address.pincode && <Typography variant="body2"><strong>Pincode:</strong> {decodeHtmlEntities(address.pincode)}</Typography>}
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Education */}
          {actualData.education.length > 0 && (
            <Card sx={{ mb: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <EducationIcon sx={{ mr: 2, fontSize: '2rem' }} />
                  Education ({actualData.education.length})
                </Typography>
                <Grid container spacing={3}>
                  {actualData.education.map((edu, index) => (
                    <Grid item xs={12} md={6} key={edu.id || index}>
                      <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                            <EducationIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="h6" gutterBottom>
                              {decodeHtmlEntities(edu.qualification || edu.name)}
                            </Typography>
                            <Typography variant="body2" color="primary" fontWeight="medium">
                              {edu.institute}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Grid container spacing={1}>
                          {edu.course && (
                            <Grid item xs={12}>
                              <Typography variant="body2"><strong>Course:</strong> {decodeHtmlEntities(edu.course)}</Typography>
                            </Grid>
                          )}
                          {edu.board && (
                            <Grid item xs={12}>
                              <Typography variant="body2"><strong>Board/University:</strong> {decodeHtmlEntities(edu.board)}</Typography>
                            </Grid>
                          )}
                          {(edu.startyear_c || edu.endyear_c) && (
                            <Grid item xs={12}>
                              <Typography variant="body2">
                                <strong>Duration:</strong> {edu.startyear_c} - {edu.endyear_c}
                              </Typography>
                            </Grid>
                          )}
                          {edu.grade && (
                            <Grid item xs={12}>
                              <Typography variant="body2"><strong>Grade/Percentage:</strong> {decodeHtmlEntities(edu.grade)}</Typography>
                            </Grid>
                          )}
                          {edu.instituecity && (
                            <Grid item xs={12}>
                              <Typography variant="body2">
                                <strong>Location:</strong> {decodeHtmlEntities([edu.instituecity, edu.institutestate, edu.institutecountry].filter(Boolean).join(', '))}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>

                        {/* Education Image */}
                        {edu.image && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Document:
                            </Typography>
                            <img 
                              src={edu.image} 
                              alt="Education Document"
                              style={{ 
                                maxWidth: '100%', 
                                maxHeight: 200, 
                                borderRadius: 8,
                                border: '1px solid #e0e0e0'
                              }}
                              onError={(e) => {
                                e.target.closest('div').style.display = 'none';
                              }}
                            />
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Work Experience */}
          {actualData.workDetails.length > 0 && (
            <Card sx={{ mb: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <WorkIcon sx={{ mr: 2, fontSize: '2rem' }} />
                  Work Experience ({actualData.workDetails.length})
                </Typography>
                <List>
                  {actualData.workDetails.map((work, index) => (
                    <React.Fragment key={work.id || index}>
                      <ListItem sx={{ px: 0, py: 2 }}>
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: 'secondary.main' }}>
                            <WorkIcon />
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                              <Box component="span" sx={{ fontSize: '1.25rem', fontWeight: 'bold', lineHeight: 1.2 }}>
                                {decodeHtmlEntities(work.designation)}
                              </Box>
                              {work.currentlyworking === '1' && (
                                <Chip label="Current" color="success" size="small" />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              <Box component="span" sx={{ fontSize: '1rem', color: 'primary.main', fontWeight: 500, display: 'block', mb: 1 }}>
                                {decodeHtmlEntities(work.companyname)}
                              </Box>
                              
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                  <Box component="span" sx={{ fontSize: '0.875rem' }}>
                                    <strong>Sector:</strong> {decodeHtmlEntities(work.sector)}
                                  </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Box component="span" sx={{ fontSize: '0.875rem' }}>
                                    <strong>Department:</strong> {decodeHtmlEntities(work.department)}
                                  </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Box component="span" sx={{ fontSize: '0.875rem' }}>
                                    <strong>Employment Type:</strong> {decodeHtmlEntities(work.employmenttype)}
                                  </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Box component="span" sx={{ fontSize: '0.875rem' }}>
                                    <strong>Location:</strong> {decodeHtmlEntities([work.city, work.state, work.country].filter(Boolean).join(', '))}
                                  </Box>
                                </Grid>
                                {work.description && (
                                  <Grid item xs={12}>
                                    <Box component="span" sx={{ fontSize: '0.875rem' }}>
                                      <strong>Description:</strong> {decodeHtmlEntities(work.description)}
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>

                              {/* Work Image */}
                              {work.image && (
                                <Box sx={{ mt: 2 }}>
                                  <Box component="span" sx={{ fontSize: '0.875rem', fontWeight: 500, display: 'block', mb: 1 }}>
                                    Document:
                                  </Box>
                                  <img 
                                    src={getImageUrl(work.image)} 
                                    alt="Work Document"
                                    style={{ 
                                      maxWidth: 200, 
                                      maxHeight: 150, 
                                      borderRadius: 8,
                                      border: '1px solid #e0e0e0'
                                    }}
                                    onError={(e) => {
                                      e.target.closest('div').style.display = 'none';
                                    }}
                                  />
                                </Box>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < actualData.workDetails.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}

          {/* ID Cards */}
          {actualData.idCards.length > 0 && (
            <Card sx={{ mb: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <IdCardIcon sx={{ mr: 2, fontSize: '2rem' }} />
                  ID Cards ({actualData.idCards.length})
                </Typography>
                <Grid container spacing={3}>
                  {actualData.idCards.map((idCard, index) => (
                    <Grid item xs={12} md={6} key={idCard.id || index}>
                      <Paper elevation={2} sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                            <IdCardIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="h6">
                              {decodeHtmlEntities(idCard.idtype)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {decodeHtmlEntities(idCard.name)}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Grid container spacing={1}>
                          {idCard.idnumber && (
                            <Grid item xs={12}>
                              <Typography variant="body2">
                                <strong>Number:</strong> {idCard.idnumber.startsWith('U2FsdGVkX1') ? decryptData(idCard.idnumber) : decodeHtmlEntities(idCard.idnumber)}
                              </Typography>
                            </Grid>
                          )}
                          {idCard.issuedate && (
                            <Grid item xs={12}>
                              <Typography variant="body2">
                                <strong>Issue Date:</strong> {idCard.issuedate.startsWith('U2FsdGVkX1') ? formatDate(decryptData(idCard.issuedate)) : formatDate(decodeHtmlEntities(idCard.issuedate))}
                              </Typography>
                            </Grid>
                          )}
                          {idCard.expirydate && (
                            <Grid item xs={12}>
                              <Typography variant="body2">
                                <strong>Expiry Date:</strong> {idCard.expirydate.startsWith('U2FsdGVkX1') ? formatDate(decryptData(idCard.expirydate)) : formatDate(decodeHtmlEntities(idCard.expirydate))}
                              </Typography>
                            </Grid>
                          )}
                          {idCard.description && (
                            <Grid item xs={12}>
                              <Typography variant="body2">
                                <strong>Description:</strong> {decodeHtmlEntities(idCard.description)}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>

                        {/* ID Card Images */}
                        {(idCard.image1 || idCard.image2) && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Documents:
                            </Typography>
                            <ImageList cols={2} gap={8} sx={{ maxHeight: 200 }}>
                              {[idCard.image1, idCard.image2].filter(Boolean).map((image, imgIndex) => (
                                <ImageListItem key={imgIndex}>
                                  <img 
                                    src={getImageUrl(image)} 
                                    alt={`ID Document ${imgIndex + 1}`}
                                    style={{ 
                                      width: '100%',
                                      height: 120,
                                      objectFit: 'cover',
                                      borderRadius: 8,
                                      border: '1px solid #e0e0e0'
                                    }}
                                    onError={(e) => {
                                      e.target.closest('.MuiImageListItem-root').style.display = 'none';
                                    }}
                                  />
                                </ImageListItem>
                              ))}
                            </ImageList>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Bank Details */}
          {actualData.bankDetails.length > 0 && (
            <Card sx={{ mb: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <BankIcon sx={{ mr: 2, fontSize: '2rem' }} />
                  Bank Accounts ({actualData.bankDetails.length})
                </Typography>
                <Grid container spacing={3}>
                  {actualData.bankDetails.map((bank, index) => (
                    <Grid item xs={12} md={6} lg={4} key={bank.id || index}>
                      <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                            <BankIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="h6">
                              {decodeHtmlEntities(bank.bankname)}
                            </Typography>
                            <Chip 
                              label={bank.status} 
                              size="small"
                              color={bank.status === 'Active' ? 'success' : 'default'}
                            />
                          </Box>
                        </Box>
                        
                        <Grid container spacing={1}>
                          {bank.accountholdername && (
                            <Grid item xs={12}>
                              <Typography variant="body2">
                                <strong>Account Holder:</strong> {decodeHtmlEntities(bank.accountholdername)}
                              </Typography>
                            </Grid>
                          )}
                          {bank.accountnumber && (
                            <Grid item xs={12}>
                              <Typography variant="body2">
                                <strong>Account Number:</strong> {bank.accountnumber.startsWith('U2FsdGVkX1') ? decryptData(bank.accountnumber) : decodeHtmlEntities(bank.accountnumber)}
                              </Typography>
                            </Grid>
                          )}
                          {bank.ifsc && (
                            <Grid item xs={12}>
                              <Typography variant="body2">
                                <strong>IFSC Code:</strong> {bank.ifsc.startsWith('U2FsdGVkX1') ? decryptData(bank.ifsc) : decodeHtmlEntities(bank.ifsc)}
                              </Typography>
                            </Grid>
                          )}
                          {bank.branch && (
                            <Grid item xs={12}>
                              <Typography variant="body2">
                                <strong>Branch:</strong> {decodeHtmlEntities(bank.branch)}
                              </Typography>
                            </Grid>
                          )}
                          {bank.accounttype && (
                            <Grid item xs={12}>
                              <Typography variant="body2">
                                <strong>Account Type:</strong> {decodeHtmlEntities(bank.accounttype)}
                              </Typography>
                            </Grid>
                          )}
                          {bank.description && (
                            <Grid item xs={12}>
                              <Typography variant="body2">
                                <strong>Description:</strong> {decodeHtmlEntities(bank.description)}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>

                        {/* Bank Document Image */}
                        {bank.image && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Document:
                            </Typography>
                            <img 
                              src={getImageUrl(bank.image)} 
                              alt="Bank Document"
                              style={{ 
                                maxWidth: '100%', 
                                maxHeight: 150, 
                                borderRadius: 8,
                                border: '1px solid #e0e0e0'
                              }}
                              onError={(e) => {
                                e.target.closest('div').style.display = 'none';
                              }}
                            />
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}


          {/* No Data Message */}
          {!actualData.basicInfo && 
           actualData.education.length === 0 &&
           actualData.workDetails.length === 0 &&
           actualData.idCards.length === 0 &&
           actualData.bankDetails.length === 0 &&
           actualData.addressDetails.length === 0 && (
            <Alert severity="info">
              No information has been shared in this profile.
            </Alert>
          )}
        </Box>
      )}
    </Container>
  );
};

export default SharedProfilePage;